import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, push, set, update } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('cash');
  const [processing, setProcessing] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);

  const ride = {
    requestId: searchParams.get('requestId') || '',
    from: searchParams.get('from') || '—',
    to: searchParams.get('to') || '—',
    date: searchParams.get('date') || '—',
    driver: searchParams.get('driver') || '—',
    driverPhone: searchParams.get('driverPhone') || '',
    seats: parseInt(searchParams.get('seats')) || 1,
    pricePerSeat: parseInt(searchParams.get('pricePerSeat')) || 0,
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  const totalAmount = ride.seats * ride.pricePerSeat;
  const platformFee = Math.round(totalAmount * 0.02);
  const gst = Math.round(platformFee * 0.18);
  const grandTotal = totalAmount + platformFee + gst;

  const confirmCashBooking = async () => {
    setProcessing(true);
    try {
      const newPaymentRef = push(ref(db, 'payments/'));
      await set(newPaymentRef, {
        from: ride.from,
        to: ride.to,
        date: ride.date,
        driver: ride.driver,
        driverPhone: ride.driverPhone,
        seats: ride.seats,
        amount: grandTotal,
        method: 'cash',
        status: 'cash_pending',
        passengerName: user.name,
        passengerPhone: user.phone,
        timestamp: new Date().toISOString(),
      });
      setProcessing(false);
      setPaymentDone(true);
    } catch (error) {
      setProcessing(false);
      alert('Error: ' + error.message);
    }
  };

  if (!user) return null;

  if (paymentDone) {
    return (
      <div style={styles.page}>
        <Navbar userName={user.name} userPhone={user.phone} />
        <div style={styles.successWrap}>
          <div style={styles.successIcon}>🎫</div>
          <h2 style={styles.successTitle}>Booking Confirmed!</h2>
          <div style={styles.successAmount}>₹{grandTotal}</div>
          <p style={styles.successSub}>{ride.from} → {ride.to} · {ride.seats} seats</p>

          <div style={styles.cashWarningBox}>
            <p style={styles.cashWarningTitle}>💵 Pay cash to driver</p>
            <p style={styles.cashWarningText}>
              Your seat is reserved. Please pay ₹{grandTotal} directly to {ride.driver} at pickup.
            </p>
          </div>

          <button style={styles.doneBtn} onClick={() => navigate('/my-bookings')}>View My Bookings</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>💳 Payment</h2>
        <p style={styles.pageSub}>Complete your booking</p>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Booking Summary</h3>
          <div style={styles.row}><span style={styles.label}>Route</span><span style={styles.value}>{ride.from} → {ride.to}</span></div>
          <div style={styles.row}><span style={styles.label}>Date</span><span style={styles.value}>{ride.date}</span></div>
          <div style={styles.row}><span style={styles.label}>Driver</span><span style={styles.value}>{ride.driver}</span></div>
          <div style={styles.row}><span style={styles.label}>Seats</span><span style={styles.value}>{ride.seats}</span></div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Bill Details</h3>
          <div style={styles.row}><span style={styles.label}>₹{ride.pricePerSeat} × {ride.seats} seats</span><span style={styles.value}>₹{totalAmount}</span></div>
          <div style={styles.row}><span style={styles.label}>Platform fee + GST</span><span style={styles.value}>₹{platformFee + gst}</span></div>
          <div style={{ ...styles.row, borderTop: '1px solid #eef1f8', paddingTop: 10, marginTop: 6 }}>
            <span style={styles.totalLabel}>Total Payable</span>
            <span style={styles.totalVal}>₹{grandTotal}</span>
          </div>
        </div>

        <div style={styles.cashReminderBox}>
          <p style={styles.cashReminderTitle}>💵 Carry exact change</p>
          <p style={styles.cashReminderText}>Pay ₹{grandTotal} in cash directly to {ride.driver} at pickup</p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Payment Method</h3>
          <div style={styles.methodRow}>
            <div style={styles.methodIcon}>💵</div>
            <div style={{ flex: 1 }}>
              <div style={styles.methodName}>Cash</div>
              <div style={styles.methodDesc}>Pay driver directly</div>
            </div>
            <div style={styles.radioDot}></div>
          </div>
          <div style={{ ...styles.methodRow, opacity: 0.5 }}>
            <div style={styles.methodIcon}>📱</div>
            <div style={{ flex: 1 }}>
              <div style={styles.methodName}>UPI</div>
              <div style={styles.methodDesc}>Coming soon</div>
            </div>
          </div>
        </div>

        <button style={styles.payBtn} onClick={confirmCashBooking} disabled={processing}>
          {processing ? 'Processing...' : '✅ Confirm Booking · ₹' + grandTotal + ' cash pending'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 520, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', margin: '0 0 4px' },
  pageSub: { color: '#5a5f7d', fontSize: 13, marginBottom: 20 },
  card: { border: '1px solid #eef1f8', borderRadius: 14, padding: 18, marginBottom: 14 },
  cardTitle: { margin: '0 0 12px', color: '#1a1a2e', fontSize: 15 },
  row: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 },
  label: { fontSize: 13, color: '#5a5f7d' },
  value: { fontSize: 13, fontWeight: 600, color: '#1a1a2e' },
  totalLabel: { fontSize: 15, fontWeight: 700, color: '#1a1a2e' },
  totalVal: { fontSize: 20, fontWeight: 800, color: '#3a8fd0' },
  cashReminderBox: { background: '#fdf3e3', border: '1px solid #f0c674', borderRadius: 10, padding: 12, marginBottom: 14 },
  cashReminderTitle: { fontSize: 13, fontWeight: 700, color: '#8a6416', margin: '0 0 2px' },
  cashReminderText: { fontSize: 12, color: '#8a6416', margin: 0 },
  methodRow: { display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 10, border: '1.5px solid #3a8fd0', background: '#eaf5fc', marginBottom: 8 },
  methodIcon: { fontSize: 22 },
  methodName: { fontSize: 14, fontWeight: 600, color: '#1a1a2e' },
  methodDesc: { fontSize: 11, color: '#9499b5', marginTop: 1 },
  radioDot: { width: 18, height: 18, borderRadius: 9, background: '#3a8fd0' },
  payBtn: { width: '100%', height: 52, background: '#f0a53a', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  successWrap: { maxWidth: 420, margin: '60px auto', padding: '0 20px', textAlign: 'center' },
  successIcon: { fontSize: 60, marginBottom: 10 },
  successTitle: { color: '#1a1a2e', margin: '0 0 6px' },
  successAmount: { fontSize: 32, fontWeight: 800, color: '#f0a53a', marginBottom: 10 },
  successSub: { color: '#5a5f7d', marginBottom: 20 },
  cashWarningBox: { background: '#fdf3e3', border: '1.5px solid #f0c674', borderRadius: 12, padding: 16, marginBottom: 20, textAlign: 'left' },
  cashWarningTitle: { fontSize: 14, fontWeight: 700, color: '#8a6416', margin: '0 0 4px' },
  cashWarningText: { fontSize: 12, color: '#8a6416', margin: 0, lineHeight: 1.5 },
  doneBtn: { width: '100%', height: 50, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};